/**
 * Field names whose values must never reach Sentry. PDPA-protected (Thai)
 * identity + contact fields plus auth credentials. Matched by exact key name
 * (case-insensitive) and replaced with "[REDACTED]".
 */
export const PII_KEYS: ReadonlySet<string> = new Set([
	"email",
	"phone",
	"phonenumber",
	"mobile",
	"licenseno",
	"licensenumber",
	"documentid",
	"documentno",
	"idcard",
	"nationalid",
	"passport",
	"taxid",
	"firstname",
	"lastname",
	"fullname",
	"address",
	"birthday",
	"dob",
	"cookie",
	"authorization",
	"password",
]);

export const REDACTED = "[REDACTED]";

const MAX_DEPTH = 6;

/**
 * Returns a structurally-rebuilt copy with PII keys redacted. On depth overflow
 * or cycles the offending sub-tree is dropped entirely (returns `undefined`) so
 * PII can never survive the recursion limit.
 */
export function scrub<T>(value: T): T {
	return scrubInternal(value, 0, new WeakSet()) as T;
}

function scrubInternal(
	value: unknown,
	depth: number,
	seen: WeakSet<object>,
): unknown {
	if (value == null || typeof value !== "object") return value;
	if (depth > MAX_DEPTH) return undefined;
	if (seen.has(value)) return undefined;
	seen.add(value);

	if (Array.isArray(value)) {
		return value.map((v) => scrubInternal(v, depth + 1, seen));
	}

	const out: Record<string, unknown> = {};
	for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
		out[k] = PII_KEYS.has(k.toLowerCase())
			? REDACTED
			: scrubInternal(v, depth + 1, seen);
	}
	return out;
}

const URL_QUERY = /\?[^\s#]*/g;
const URL_FRAGMENT = /#[^\s]*/g;

/** Drops `?…` and `#…` from URL-bearing breadcrumb messages (fetch/xhr/navigation). */
export function redactMessageUrls(message: string): string {
	return message
		.replace(URL_QUERY, "?[REDACTED]")
		.replace(URL_FRAGMENT, "#[REDACTED]");
}
